import { useState, useEffect, memo, useCallback, type CSSProperties } from 'react'
import { programGenresApi } from '../../lib/api.js'
import { useStore } from '../../store/index.js'
import type { ProgramGenre } from '../../types/domain.js'
import { ProgramGenreModal } from './ProgramGenreModal.js'
import { ProgramGenreMergeModal } from './ProgramGenreMergeModal.js'
import { TaxonomyReorderButtons } from './TaxonomyReorderButtons.js'
import { Spinner } from '../common/Spinner.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramGenresPanelProps {
  channelId: string
  canManage?: boolean
}

// ─── Composant ────────────────────────────────────────────────────────────────

/**
 * Gestion des genres éditoriaux : la liste fermée publiée au guide des
 * programmes. Renommer se propage seul — les fiches référencent la ligne —,
 * tandis que fusionner déplace réellement des fiches et supprime un genre.
 */
export const ProgramGenresPanel = memo(function ProgramGenresPanel({ channelId, canManage = true }: ProgramGenresPanelProps) {
  const programsUpdateSeq = useStore(state => state.programsUpdateSeq)
  const [genres,    setGenres]    = useState<ProgramGenre[]>([])
  const [loading,   setLoading]   = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing,   setEditing]   = useState<ProgramGenre | undefined>(undefined)
  const [merging,   setMerging]   = useState<ProgramGenre | undefined>(undefined)
  const [search,    setSearch]    = useState('')
  const [sortOrder, setSortOrder] = useState<'manual' | 'name-asc' | 'name-desc' | 'count-desc'>('manual')
  const [error,     setError]     = useState<string | null>(null)

  const reload = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    programGenresApi.list({ channelId })
      .then(setGenres)
      .finally(() => setLoading(false))
  }, [channelId])

  useEffect(() => { reload(genres.length > 0) }, [reload, programsUpdateSeq])

  const handleCreated = useCallback((genre: ProgramGenre) => {
    setGenres(prev => [...prev, genre].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)))
    setShowModal(false)
  }, [])

  const handleUpdated = useCallback((genre: ProgramGenre) => {
    setGenres(prev => prev.map(g => g.id === genre.id ? genre : g))
    setEditing(undefined)
    setShowModal(false)
  }, [])

  const handleDeleted = useCallback((id: string) => {
    setGenres(prev => prev.filter(g => g.id !== id))
  }, [])

  const handleEdit = useCallback((genre: ProgramGenre, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(genre)
    setShowModal(true)
  }, [])

  const handleMerge = useCallback((genre: ProgramGenre, e: React.MouseEvent) => {
    e.stopPropagation()
    setMerging(genre)
  }, [])

  // La fusion déplace des fiches et supprime une ligne : on relit la liste
  // complète plutôt que de deviner les nouveaux compteurs.
  const handleMerged = useCallback(() => {
    setMerging(undefined)
    reload(true)
  }, [reload])

  /**
   * Réordonnancement optimiste : l'ordre se repose tout de suite et se rétablit
   * si l'API refuse. Attendre l'aller-retour rendrait le réglage d'une longue
   * liste pénible.
   */
  const handleReorder = useCallback(async (nextIds: string[]) => {
    const previous = genres
    const byId = new Map(genres.map(genre => [genre.id, genre]))
    setGenres(nextIds.map((id, index) => ({ ...byId.get(id)!, sortOrder: index })))
    setError(null)
    try {
      await programGenresApi.reorder(nextIds[0], nextIds)
    } catch (cause) {
      setGenres(previous)
      setError(cause instanceof Error ? cause.message : 'Réordonnancement impossible')
    }
  }, [genres])

  const handleNew = useCallback(() => {
    setEditing(undefined)
    setShowModal(true)
  }, [])

  // L'ordre manuel se pose sur la liste complète, jamais sur un résultat filtré
  // ou trié autrement : sinon la permutation envoyée ne correspondrait pas à ce
  // que l'opérateur voit.
  const manualIds = [...genres]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr'))
    .map(genre => genre.id)
  const searching = search.trim().length > 0
  const canReorder = canManage && sortOrder === 'manual' && !searching

  const filtered = genres.filter(g =>
    !search || g.label.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (sortOrder === 'name-asc') return a.label.localeCompare(b.label, 'fr')
    if (sortOrder === 'name-desc') return b.label.localeCompare(a.label, 'fr')
    if (sortOrder === 'count-desc') return (b._count?.programs ?? 0) - (a._count?.programs ?? 0)
    return a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr')
  })

  return (
    <div className="catalog-list">

      {/* Toolbar */}
      <div className="catalog-toolbar catalog-toolbar--stack">
        <div className="catalog-search-row">
          <input
            type="text"
            placeholder="Rechercher un genre…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="tp-input catalog-search"
            aria-label="Rechercher un genre"
          />
          <button
            type="button"
            onClick={handleNew}
            disabled={!canManage}
            title={!canManage ? 'Permission de gestion des collections et séries requise' : undefined}
            className="tp-button tp-button--info"
          >
            + Nouveau
          </button>
        </div>

        <div className="catalog-filter-dropdowns">
          <select
            className="tp-select catalog-filter-select"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value as any)}
            aria-label="Trier les genres"
          >
            <option value="manual">Ordre de tri manuel</option>
            <option value="name-asc">Nom (A-Z)</option>
            <option value="name-desc">Nom (Z-A)</option>
            <option value="count-desc">Nombre de programmes (décroissant)</option>
          </select>
        </div>
      </div>

      <p className="catalog-empty-copy">
        Le genre est publié au guide des programmes. Le renommer met à jour toutes les fiches qui le
        portent : elles référencent le genre, elles n’en recopient pas le nom.
      </p>

      {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

      {/* Compteur */}
      <div className="catalog-count" role="status">
        {loading ? <><Spinner /> Chargement…</> : `${filtered.length} genre${filtered.length > 1 ? 's' : ''}`}
      </div>

      {/* Liste */}
      <div className="catalog-scroll">
        {loading ? null : filtered.length === 0 ? (
          <div className="catalog-empty">
            {genres.length === 0
              ? 'Aucun genre. Créez-en un pour classer vos programmes (Musique, Sport, Société & Économie…).'
              : 'Aucun résultat'}
          </div>
        ) : (
          filtered.map(genre => (
            <ProgramGenreRow
              key={genre.id}
              genre={genre}
              orderedIds={manualIds}
              canReorder={canReorder}
              reorderHint={searching
                ? 'Effacez la recherche pour réordonner'
                : sortOrder !== 'manual'
                  ? 'Choisissez « Ordre de tri manuel » pour réordonner'
                  : undefined}
              onReorder={ids => void handleReorder(ids)}
              onEdit={(e) => handleEdit(genre, e)}
              onMerge={(e) => handleMerge(genre, e)}
              onDeleted={() => handleDeleted(genre.id)}
              canManage={canManage}
            />
          ))
        )}
      </div>

      {showModal && canManage && (
        <ProgramGenreModal
          channelId={channelId}
          genre={editing}
          onCreated={handleCreated}
          onUpdated={handleUpdated}
          onClose={() => { setShowModal(false); setEditing(undefined) }}
        />
      )}

      {merging && canManage && (
        <ProgramGenreMergeModal
          source={merging}
          genres={genres}
          onMerged={handleMerged}
          onClose={() => setMerging(undefined)}
        />
      )}
    </div>
  )
})

// ─── ProgramGenreRow ──────────────────────────────────────────────────────────

const ProgramGenreRow = memo(function ProgramGenreRow({ genre, orderedIds, canReorder, reorderHint, onReorder, onEdit, onMerge, onDeleted, canManage }: {
  genre:       ProgramGenre
  orderedIds:  string[]
  canReorder:  boolean
  reorderHint?: string
  onReorder:   (orderedIds: string[]) => void
  onEdit:      (e: React.MouseEvent) => void
  onMerge:     (e: React.MouseEvent) => void
  onDeleted:   () => void
  canManage:   boolean
}) {
  const accentStyle = genre.color
    ? { '--catalog-accent': genre.color } as CSSProperties
    : undefined
  const programCount = genre._count?.programs ?? 0
  const seriesCount  = genre._count?.series ?? 0
  const used         = programCount + seriesCount

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    // Le serveur refuse la suppression d'un genre porté par des fiches : les
    // délier les ferait partir sans rubrique au guide des programmes.
    if (used > 0) {
      alert(`« ${genre.label} » est porté par ${used} fiche${used > 1 ? 's' : ''}.\n\nUtilisez la fusion pour les rattacher à un autre genre.`)
      return
    }
    if (!confirm(`Supprimer le genre « ${genre.label} » ?`)) return
    try {
      await programGenresApi.delete(genre.id)
      onDeleted()
    } catch (err: any) {
      alert(err.message)
    }
  }

  return (
    <div className="catalog-collection-row">
      <span className="catalog-collection-row__select">
        <span className="catalog-collection-row__dot" style={accentStyle} aria-hidden="true" />
        <span className="catalog-collection-row__copy">
          <span className="catalog-collection-row__title">{genre.label}</span>
          <span className="catalog-collection-row__meta">
            {programCount} programme{programCount !== 1 ? 's' : ''}
            {seriesCount > 0 && ` · ${seriesCount} série${seriesCount !== 1 ? 's' : ''}`}
          </span>
        </span>
      </span>

      <div className="catalog-row-actions">
        <TaxonomyReorderButtons
          orderedIds={orderedIds}
          id={genre.id}
          label={genre.label}
          enabled={canReorder}
          disabledReason={reorderHint}
          onReorder={onReorder}
        />
        <button
          type="button"
          onClick={onEdit}
          disabled={!canManage}
          title={canManage ? 'Modifier' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Modifier ${genre.label}`}
          className="catalog-icon-action"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={onMerge}
          disabled={!canManage}
          title={canManage ? 'Fusionner avec un autre genre' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Fusionner ${genre.label}`}
          className="catalog-icon-action"
        >
          ⤵
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!canManage}
          title={canManage ? 'Supprimer' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Supprimer ${genre.label}`}
          className="catalog-icon-action catalog-icon-action--danger"
        >
          ×
        </button>
      </div>
    </div>
  )
})
